import { useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import type { GridColDef } from "@mui/x-data-grid";
import { api } from "../lib/api";
import { describeCheckError } from "../lib/check-error";
import { useAsync } from "../lib/hooks";
import { formatMs } from "../lib/format";
import { useI18n } from "../lib/i18n";
import type { BrandLink, Device } from "../lib/types";
import { Badge, Button, Card, Empty, ErrorBox, ErrorCell, Loading, Stat } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { ApiKeyModal, DeviceForm } from "../components/device-dialogs";
import { LinkMatrixView } from "../components/LinkMatrix";

export function Dashboard({ navigate }: { navigate: (to: string) => void }) {
  const { t, fmtDate, fmtAgo } = useI18n();
  const [tick, setTick] = useState(0);
  const links = useAsync(() => api.brandLinks.list(), [tick]);
  const report = useAsync(() => api.reports.latest(), [tick]);
  const devices = useAsync(() => api.devices.list(), [tick]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addingDevice, setAddingDevice] = useState(false);
  const [revealedKey, setRevealedKey] = useState<Device | null>(null);

  async function run(action: string, fn: () => Promise<unknown>) {
    setBusy(action);
    setError(null);
    try {
      await fn();
      setTick(v => v + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const all = links.data ?? [];
  const healthy = all.filter(l => l.healthStatus === "healthy").length;
  const unhealthy = all.filter(l => l.healthStatus === "unhealthy").length;
  const unknown = all.filter(l => l.healthStatus === "unknown").length;
  const activeDevices = (devices.data ?? []).filter(d => !d.disabled).length;

  // Link lỗi lên đầu, rồi tới link chưa rõ — phần cần chú ý nhất.
  const attention = [...all]
    .filter(l => l.healthStatus !== "healthy")
    .sort((a, b) => (a.healthStatus === "unhealthy" ? -1 : 1))
    .slice(0, 10);

  const attentionColumns = useMemo<GridColDef<BrandLink>[]>(
    () => [
      { field: "brand", headerName: t("table.brand"), flex: 0.7, minWidth: 90 },
      {
        field: "domain",
        headerName: t("table.domain"),
        flex: 1.6,
        minWidth: 160,
        renderCell: ({ row }) => (
          <Tooltip title={row.domain}>
            <span className="mono ellipsis">{row.domain}</span>
          </Tooltip>
        ),
      },
      {
        field: "healthStatus",
        headerName: t("table.status"),
        flex: 0.6,
        minWidth: 100,
        renderCell: ({ row }) => <Badge status={row.healthStatus} />,
      },
      {
        field: "lastError",
        headerName: t("table.error"),
        flex: 1.1,
        minWidth: 140,
        renderCell: ({ row }) => {
          const msg = describeCheckError(
            t,
            { code: row.lastErrorCode, statusCode: row.lastStatusCode, detail: row.lastErrorDetail },
            row.lastError,
          );
          return <ErrorCell error={msg} />;
        },
      },
      {
        field: "lastCheckerIp",
        headerName: t("table.checkerIp"),
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ row }) => (
          <Tooltip title={t("table.checkerIpHint")}>
            <span className="mono ellipsis">{row.lastCheckerIp ?? "—"}</span>
          </Tooltip>
        ),
      },
      {
        field: "healthCheckedAt",
        headerName: t("table.checkedAt"),
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Tooltip title={fmtDate(row.healthCheckedAt)}>
            <span>{fmtAgo(row.healthCheckedAt)}</span>
          </Tooltip>
        ),
      },
    ],
    [t, fmtDate, fmtAgo],
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{t("dash.title")}</h1>
          <p className="muted">{t("dash.subtitle")}</p>
        </div>
        <div className="row-actions">
          <Button onClick={() => setAddingDevice(true)}>{t("devices.quickAdd")}</Button>
          {/*
            TẮT kiểm tra trực tiếp từ CMS — việc check do thiết bị chạy và gửi
            kết quả về. Bỏ comment là nút chạy lại như cũ; endpoint bên API
            (POST /brand-links/check/all) vẫn còn nguyên.
            <Button
              loading={busy === "check"}
              onClick={() => run("check", () => api.brandLinks.checkAll())}
            >
              {t("dash.checkAll")}
            </Button>
          */}
          <Button
            variant="primary"
            loading={busy === "report"}
            onClick={() => run("report", () => api.reports.generate())}
          >
            {t("dash.generateReport")}
          </Button>
        </div>
      </header>

      {error && <ErrorBox message={error} />}
      {links.error && <ErrorBox message={links.error} />}

      <div className="stat-grid">
        <Stat label={t("dash.totalLinks")} value={all.length} />
        <Stat label={t("status.healthy")} value={healthy} tone="good" />
        <Stat label={t("status.unhealthy")} value={unhealthy} tone="bad" />
        <Stat label={t("status.unknown")} value={unknown} tone="warn" />
        <Stat label={t("dash.activeDevices")} value={activeDevices} />
      </div>

      <LinkMatrixView />

      <div className="grid-2">
        <Card
          title={t("dash.attention")}
          actions={
            <Button variant="ghost" onClick={() => navigate("links")}>
              {t("dash.viewAllLinks")}
            </Button>
          }
        >
          {links.loading ? (
            <Loading />
          ) : attention.length === 0 ? (
            <Empty message={t("dash.allHealthy")} />
          ) : (
            <DataTable rows={attention} columns={attentionColumns} compactFooter />
          )}
        </Card>

        <Card
          title={t("dash.latestReport")}
          actions={
            <Button variant="ghost" onClick={() => navigate("reports")}>
              {t("dash.viewHistory")}
            </Button>
          }
        >
          {report.loading ? (
            <Loading />
          ) : !report.data ? (
            <Empty message={t("dash.noReport")} />
          ) : (
            <div className="report-summary">
              <dl className="kv">
                <div>
                  <dt>{t("dash.generatedAt")}</dt>
                  <dd>{fmtDate(report.data.generatedAt)}</dd>
                </div>
                <div>
                  <dt>{t("table.source")}</dt>
                  <dd>{t(`trigger.${report.data.triggeredBy}`)}</dd>
                </div>
                <div>
                  <dt>{t("dash.duration")}</dt>
                  <dd>{formatMs(report.data.durationMs)}</dd>
                </div>
                <div>
                  <dt>{t("dash.ratio")}</dt>
                  <dd>
                    {report.data.totalLinks} / {report.data.healthy} /{" "}
                    {report.data.unhealthy}
                  </dd>
                </div>
              </dl>
              <HealthBar
                healthy={report.data.healthy}
                unhealthy={report.data.unhealthy}
                unknown={report.data.unknown}
              />
            </div>
          )}
        </Card>
      </div>

      {addingDevice && (
        <DeviceForm
          device={null}
          onClose={() => setAddingDevice(false)}
          onSaved={created => {
            setAddingDevice(false);
            // Key gốc chỉ trả về đúng lần này — mở modal cho chép ngay.
            if (created?.apiKey) setRevealedKey(created);
            setTick(v => v + 1);
          }}
        />
      )}

      {revealedKey?.apiKey && (
        <ApiKeyModal device={revealedKey} onClose={() => setRevealedKey(null)} />
      )}
    </div>
  );
}

export function HealthBar({
  healthy,
  unhealthy,
  unknown,
}: {
  healthy: number;
  unhealthy: number;
  unknown: number;
}) {
  const { t } = useI18n();
  const total = healthy + unhealthy + unknown || 1;
  const pct = (n: number) => `${(n / total) * 100}%`;
  const title = `${healthy} ${t("status.healthy")} · ${unhealthy} ${t("status.unhealthy")} · ${unknown} ${t("status.unknown")}`;
  return (
    <div className="health-bar" title={title}>
      <span className="seg seg-healthy" style={{ width: pct(healthy) }} />
      <span className="seg seg-unhealthy" style={{ width: pct(unhealthy) }} />
      <span className="seg seg-unknown" style={{ width: pct(unknown) }} />
    </div>
  );
}
